import { useEffect, useMemo, useState } from 'react';

export const useScreenSize = () => {
  const getSize = (): { width: number; height: number } => ({
    width: typeof window !== 'undefined' ? window.innerWidth * window.devicePixelRatio : 0,
    height: typeof window !== 'undefined' ? window.innerHeight * window.devicePixelRatio : 0,
  });

  const [effectiveSize, setEffectiveSize] = useState(getSize);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setEffectiveSize(getSize());
      }, 100);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return effectiveSize;
};

const useMediaQuery = (query: string) => {
  const mediaQuery = useMemo(() => window.matchMedia(query), [query]);
  const [match, setMatch] = useState(mediaQuery.matches);

  useEffect(() => {
    const onChange = () => setMatch(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, [mediaQuery]);

  return match;
};

const useMediaQueries = () => {
  const landscape = useMediaQuery('(orientation: landscape)');
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { width: screenWidth } = useScreenSize();
  const isNarrowDesktop = screenWidth < 1194;

  return { landscape, isDesktop, isNarrowDesktop };
};

export default useMediaQueries;
